import { List, Map } from "immutable"
import { ActivitySummaryProps } from "../custom_components/components/activity_summary"
import { ActivityClustersPageProps, CoreActivityCluster } from "../custom_components/pages/activity_clusters_page"
import { ActivityDetailPageProps } from "../custom_components/pages/activity_detail_page"
import { CollectionPageProps } from "../custom_components/pages/collection_page"
import { GeneralContentPageProps } from "../custom_components/pages/general_content_page"
import { HomePageProps } from "../custom_components/pages/home_page"
import { LocationDetailPageDrupalProps } from "../custom_components/pages/location_detail_page"
import { ReportOverviewPageProps } from "../custom_components/pages/report_overview_page"
import {
  ParsedNodeActivityClusters,
  ParsedNodeActivityDetail,
  ParsedNodeCollectionOverview,
  ParsedNodeHome,
  ParsedNodeLocationDetail,
  ParsedNodePage,
  ParsedNodeReport,
  ParsedNodeReportOverview,
  ParsedParagraph,
  ParsedNodeSections,
  ParsedNodeVacancy,
  ParsedParagraphCoreActivityCluster,
} from "../../Shared/drupal_models/types"
import { Block } from "./../custom_components/components/block_renderer"
import {
  activityClusterBlock,
  booksOverviewBlock,
  buttonBlock,
  collectionBlock,
  contentGridBlock,
  emptyBlock,
  faqBlock,
  featuredContentBlock,
  highlightBlock,
  imagesBlock,
  mediaBlock,
  richTextBlock,
  subscriptionsBlock,
  vacancyOverviewBlock,
  pageGridBlock,
  quoteBlock,
  carouselContentGridBlock,
  collectionSearchBarBlock,
  gridBlock,
  tippedActivitiesBlock,
  wordCloudBlock,
  formstackBlock,
  ctaBlock,
  newsletterSignupBlock,
  subMenuBlock,
  themakiezerBlock
} from "./drupal_paragraph_to_block"
import { Region } from "./api"

// Replace internal with external Urls (to avoid users from being unable to see images served by the internal URL)
export const fixMediaUrl = (style_url: string): string => {
  const internalUrl: string = window.DrupalInternalUrl
  const externalUrl: string = window.DrupalExternalUrl

  if (!externalUrl) return style_url
  if (style_url.startsWith('/')) return externalUrl + style_url
  if (style_url && internalUrl) return style_url.replace(internalUrl, externalUrl)

  return style_url
}

export const processRichText: (html: string) => string = html =>
  html.replace("<ul>", '<ul class="richtext__bullet-list">').replace("<ol>", '<ol class="richtext__check-list">')

export const activityClustersPage: (n: ParsedNodeActivityClusters) => ActivityClustersPageProps = n => ({
  kind: "activityClusters",
  pageTitle: n.label,
  headerTitle: n.field_header_title,
  description: n.field_description ? processRichText(n.field_description) : "",
  imageUrl: fixMediaUrl(n.field_media.field_media_image.image.style_urls["1280x720"]),
  imageAlt: n.field_media.field_media_image.alt,
  coreActivityClusters: n.field_core_activity_clusters.map(coreActivityCluster).toList(),
  coreActivityAmount: n.field_kern_activiteiten_aantal ? parseInt(n.field_kern_activiteiten_aantal) : 3,
  regions: Map<string, Region>(),
  selectedPeriod: null,
  selectedRegion: null,
  selectedCluster: 'none',
  selectPeriod: null,
  selectRegion: null,
  toggleArchive: null,
  showArchive: false,
  ctaToggle: n.field_toggle_cta,
  ctaTitle: n.field_cta_title,
  ctaDescription: n.field_cta_description,
  ctaPrimaryActionTitle: n.field_cta_primary_action?.title,
  ctaPrimaryActionUrl: n.field_cta_primary_action?.url,
  ctaSecondaryActionTitle: n.field_cta_secondary_action?.title,
  ctaSecondaryActionUrl: n.field_cta_secondary_action?.url,
  activityPageState: null,
  setActivityState: null,
  pageLanguage: n.field_meta_tags && n.field_meta_tags["content_language"] ? n.field_meta_tags["content_language"] : 'nl'
})

const coreActivityCluster = (c: ParsedParagraphCoreActivityCluster): CoreActivityCluster => ({
  id: c.field_activity_cluster_id,
  title: c.field_title,
  description: c.field_text ? c.field_text : '',
  imageUrl: fixMediaUrl(c.field_image.field_media_image.image.style_urls["790w"]),
  imageAlt: c.field_image.field_media_image.alt,
  linkUrl: c.field_link.url,
  linkText: c.field_link.title
})

export const activityDetailPage: (n: ParsedNodeActivityDetail) => ActivityDetailPageProps = n => ({
  kind: "activityDetail",
  activityId: n.field_product_db_id,
  pageTitle: n.label,
  headerTitle: n.field_header_title,
  subtitle: n.field_short_description,
  description: n.field_description ? processRichText(n.field_description) : "",
  imageUrl: fixMediaUrl(n.field_media.field_media_image.image.style_urls["1280x720"]),
  imageAlt: n.field_media.field_media_image.alt,
  noticeTitle: n.field_notice_title,
  noticeContents: n.field_notice_contents,
  regions: Map<string, Region>(),
  selectedPeriod: null,
  selectedRegion: null,
  selectPeriod: null,
  selectRegion: null,
  blocks: generalContentPageBlocks(n.field_paragraphs),
  pageLanguage: n.field_meta_tags && n.field_meta_tags["content_language"] ? n.field_meta_tags["content_language"] : 'nl'
})

export const collectionPage: (n: ParsedNodeCollectionOverview) => CollectionPageProps = n => ({
  kind: "collection",
  pageTitle: n.label,
  headerTitle: n.field_header_title,
  description: n.field_description ? processRichText(n.field_description) : "",
  imageUrl: fixMediaUrl(n.field_media.field_media_image.image.style_urls["1280x720"]),
  imageAlt: n.field_media.field_media_image.alt,
  buttonUrl: n.field_header_button_url ? n.field_header_button_url.url : "",
  buttonTitle: n.field_header_button_url ? n.field_header_button_url.title : "",
  header_blocks: generalContentPageBlocks(n.field_paragraphs_header),
  blocks: generalContentPageBlocks(n.field_paragraphs),
  pageLanguage: n.field_meta_tags && n.field_meta_tags["content_language"] ? n.field_meta_tags["content_language"] : 'nl'
})

export const locationDetailPage: (n: ParsedNodeLocationDetail) => LocationDetailPageDrupalProps = n => ({
  kind: "locationDetail",
  locationId: n.field_product_db_id,
  pageTitle: n.label,
  headerTitle: n.field_header_title,
  subtitle: n.field_short_description,
  description: n.field_description ? processRichText(n.field_description) : "",
  imageUrl: fixMediaUrl(n.field_media.field_media_image.image.style_urls["1280x720"]),
  imageAlt: n.field_media.field_media_image.alt,
  currentTitle: n.field_current_title,
  currentContents: n.field_current_contents ? processRichText(n.field_current_contents) : "",
  aboutTitle: n.field_about_title,
  aboutContents: n.field_about_contents ? processRichText(n.field_about_contents) : "",
  openingHours: n.field_location_opening_hours,
  blocks: generalContentPageBlocks(n.field_paragraphs),
  contactCustomerSupport: n.field_contact_customer_service ? processRichText(n.field_contact_customer_service) : "",
  email: n.field_email ?? "",
  phoneNumber: n.field_telefoonnummer ?? "",
  address: n.field_address,
  selectedLocationId: "0",
  branchLocations: [],
  selectLocation: undefined,
  googlePlacesId: n.field_google_placeid,
  pageLanguage: n.field_meta_tags && n.field_meta_tags["content_language"] ? n.field_meta_tags["content_language"] : 'nl'
})

export const homePage: (n: ParsedNodeHome) => HomePageProps = n => ({
  kind: "home",
  pageTitle: n.label,
  headerTitle: n.field_header_title,
  description: n.field_description ? processRichText(n.field_description) : "",
  headerButtonUrl: n.field_header_button_url ? n.field_header_button_url.url : "",
  headerButtonTitle: n.field_header_button_url ? n.field_header_button_url.title : "",
  imageUrl: fixMediaUrl(n.field_media.field_media_image.image.style_urls["1280w"]),
  imageAlt: n.field_media.field_media_image.alt,
  blocks: generalContentPageBlocks(n.field_paragraphs),
  showAnnouncement: n.field_show_announcement,
  announcement: n.field_announcement,
  selectedLocationId: "",
  selectLocation: undefined,
  branchLocations: [],
  pageLanguage: n.field_meta_tags && n.field_meta_tags["content_language"] ? n.field_meta_tags["content_language"] : 'nl'
})

export const generalContentPage: (n: ParsedNodePage | ParsedNodeReport | ParsedNodeSections | ParsedNodeVacancy) => GeneralContentPageProps = n => ({
  kind: "generalContent",
  pageTitle: n.label,
  headerTitle: n.field_header_title,
  description: n.field_description ? processRichText(n.field_description) : "",
  imageUrl: n.field_media ? fixMediaUrl(n.field_media.field_media_image.image.style_urls["1280x720"]) : "",
  imageAlt: n.field_media ? n.field_media.field_media_image.alt : "",
  blocks: generalContentPageBlocks(n.field_paragraphs),
  buttonUrl: n.field_header_button_url ? n.field_header_button_url.url : undefined,
  buttonTitle: n.field_header_button_url ? n.field_header_button_url.title : undefined,
  jumboSize: n.field_jumbo_small ? "small" : "large",
  pageLanguage: n.field_meta_tags && n.field_meta_tags["content_language"] ? n.field_meta_tags["content_language"] : 'nl'
})

export const generalContentPageBlocks: (paragraphs: List<ParsedParagraph>) => List<Block> = paragraphs => {
  return List<Block>(
    paragraphs.map(p =>
      p.bundle == "text" 
        ? richTextBlock(p)

      : p.bundle == "media" 
        ? mediaBlock(p)

      : p.bundle == "faq_collection" 
        ? faqBlock(p)

      : p.bundle == "highlight" 
        ? highlightBlock(p)

      : p.bundle == "knop" 
        ? buttonBlock(p)

      : p.bundle == "images" 
        ? imagesBlock(p)

      : p.bundle == "content_grid" 
        ? contentGridBlock(p)

      : p.bundle == "caroussel" 
        ? carouselContentGridBlock(p)

      : p.bundle == "collection"
        ? collectionBlock(p)
        
      : p.bundle == "subscription_block"
        ? subscriptionsBlock(p)
        
      : p.bundle == "books"
        ? booksOverviewBlock(p)

      : p.bundle == "themakiezer_embed"
        ? themakiezerBlock(p)

      : p.bundle == "vacancy_overview"
        ? vacancyOverviewBlock(p)

      : p.bundle == "featured_content"
        ? featuredContentBlock(p)

      : p.bundle == "activity_cluster"
        ? activityClusterBlock(p)
        
      : p.bundle == "quote"
        ? quoteBlock(p)
      
      : p.bundle == "collection_search_bar"
        ? collectionSearchBarBlock(p)

      : p.bundle == "page_grid"
        ? pageGridBlock(p)
      
      : p.bundle == "grid_block"
        ? gridBlock(p)

      : p.bundle == "tipped_activities_block"
        ? tippedActivitiesBlock(p)

      : p.bundle == "wordcloud_block"
        ? wordCloudBlock(p)
        
      : p.bundle == "formstack_block"
        ? formstackBlock(p)

      : p.bundle == "cta_block"
        ? ctaBlock(p)
      
      : p.bundle == "newsletter_signup_form"
        ? newsletterSignupBlock(p)

      : p.bundle == "submenu"
        ? subMenuBlock(p)
        
      : emptyBlock()
    )
  )
}

export const activitySummary: (n: ActivityDetailPageProps) => ActivitySummaryProps = n => ({
  headingLevel: 'h3',
  title: n.pageTitle,
  subtitle: n.subtitle,
  date: null,
  start: null,
  end: null,
  location: null,
  imageUrl: fixMediaUrl(n.imageUrl),
  imageAlt: n.imageAlt,
  route: null,
  isTip: false
})

export const reportOverview: (n: ParsedNodeReportOverview) => ReportOverviewPageProps = n => ({
  kind: "reportOverview",
  pageTitle: n.label,
  headerTitle: n.field_header_title,
  description: n.field_description ? processRichText(n.field_description) : "",
  imageUrl: fixMediaUrl(n.field_media.field_media_image.image.style_urls["1280x720"]),
  imageAlt: n.field_media.field_media_image.alt,
  jumboSize: n.field_jumbo_small ? "small" : "large",
  buttonUrl: n.field_header_button_url ? n.field_header_button_url.url : undefined,
  buttonTitle: n.field_header_button_url ? n.field_header_button_url.title : undefined,
  row_count: n.field_row_count ? n.field_row_count : 3,
  pageLanguage: n.field_meta_tags && n.field_meta_tags["content_language"] ? n.field_meta_tags["content_language"] : 'nl'
})
